import {
  BattleCardView,
  DeckBattleCancelledPayload,
  DeckBattleResumePayload,
  DeckBattleStartedPayload,
  DeckMatchResultPayload,
  DeckPhase,
  DeckRoundResultPayload,
} from "@/types/battle"
import { mergeCardShardSlots } from "@/utils/battleCards"
import { resolveFirstSlotIsYou } from "./mapDeckRoundResult"

export type DeckDuelUiPhase =
  | "idle"
  | "play_cards"
  | "play_cards_waiting"
  | "round_reveal"
  | "match_end"
  | "cancelled"

export type DeckDuelAction =
  | { type: "RESET" }
  | { type: "DECK_BATTLE_STARTED"; payload: DeckBattleStartedPayload }
  | { type: "DECK_BATTLE_SYNC"; payload: DeckBattleStartedPayload }
  | { type: "DECK_BATTLE_RESUME"; payload: DeckBattleResumePayload }
  | { type: "PLAY_CARD"; payload: { card: BattleCardView } }
  | { type: "PLAY_CARD_FAILED" }
  | { type: "PLAY_CARD_WAITING" }
  | { type: "OPPONENT_CARD_READY" }
  | { type: "SOCKET_RECONNECTING" }
  | { type: "SOCKET_RECONNECTED" }
  | { type: "OPPONENT_DISCONNECTED"; payload: { graceSec: number } }
  | { type: "OPPONENT_RECONNECTED" }
  | {
      type: "ROUND_RESULT"
      payload: DeckRoundResultPayload
      youUserId: number
    }
  | { type: "ROUND_REVEAL_DONE" }
  | { type: "MATCH_RESULT"; payload: DeckMatchResultPayload }
  | { type: "BATTLE_CANCELLED"; payload: DeckBattleCancelledPayload }
  | { type: "TICK_TIMER" }

export interface DeckDuelState {
  uiPhase: DeckDuelUiPhase
  serverPhase: DeckPhase
  battle: DeckBattleStartedPayload | null
  yourDeck: BattleCardView[]
  usedCardIds: string[]
  currentRound: number
  yourRoundWins: number
  opponentRoundWins: number
  roundsToWin: number
  /** Владелец слота A (firstCard) */
  firstPlayerUserId: number | null
  yourPlayedCard: BattleCardView | null
  /** Соперник выбрал карту вслепую (без тела карты) */
  opponentReady: boolean
  lastRoundResult: DeckRoundResultPayload | null
  matchResult: DeckMatchResultPayload | null
  cancelled: DeckBattleCancelledPayload | null
  timerSec: number
  deadlineAt: number | null
  yourRemainingCards: number
  opponentRemainingCards: number
  /** Свой сокет переподключается — не уходить с экрана */
  socketReconnecting: boolean
  /** Соперник в grace reconnect */
  opponentDisconnected: boolean
  opponentReconnectGraceSec: number
}

export const initialDeckDuelState: DeckDuelState = {
  uiPhase: "idle",
  serverPhase: "PLAY_CARDS",
  battle: null,
  yourDeck: [],
  usedCardIds: [],
  currentRound: 1,
  yourRoundWins: 0,
  opponentRoundWins: 0,
  roundsToWin: 3,
  firstPlayerUserId: null,
  yourPlayedCard: null,
  opponentReady: false,
  lastRoundResult: null,
  matchResult: null,
  cancelled: null,
  timerSec: 0,
  deadlineAt: null,
  yourRemainingCards: 6,
  opponentRemainingCards: 6,
  socketReconnecting: false,
  opponentDisconnected: false,
  opponentReconnectGraceSec: 60,
}

function availableCards(state: DeckDuelState): BattleCardView[] {
  const used = new Set(state.usedCardIds)
  if (state.yourPlayedCard) used.add(state.yourPlayedCard.id)
  return state.yourDeck.filter((card) => !used.has(card.id))
}

function incrementRoundWins(
  state: DeckDuelState,
  roundWinnerUserId: number | null,
  youUserId: number
): { yourRoundWins: number; opponentRoundWins: number } {
  const opponentUserId = state.battle?.opponent.userId
  if (roundWinnerUserId == null) {
    return {
      yourRoundWins: state.yourRoundWins,
      opponentRoundWins: state.opponentRoundWins,
    }
  }
  if (roundWinnerUserId === youUserId) {
    return {
      yourRoundWins: state.yourRoundWins + 1,
      opponentRoundWins: state.opponentRoundWins,
    }
  }
  if (opponentUserId != null && roundWinnerUserId === opponentUserId) {
    return {
      yourRoundWins: state.yourRoundWins,
      opponentRoundWins: state.opponentRoundWins + 1,
    }
  }
  return {
    yourRoundWins: state.yourRoundWins,
    opponentRoundWins: state.opponentRoundWins,
  }
}

function resolveFirstPlayerUserId(
  payloadFirst: number,
  stateFirst: number | null
): number | null {
  if (payloadFirst > 0) return payloadFirst
  if (stateFirst != null && stateFirst > 0) return stateFirst
  return null
}

function resetRoundPlayState(): Pick<
  DeckDuelState,
  "yourPlayedCard" | "opponentReady"
> {
  return {
    yourPlayedCard: null,
    opponentReady: false,
  }
}

function startPlayCardsPhase(
  state: DeckDuelState,
  overrides: Partial<DeckDuelState> = {}
): DeckDuelState {
  const playTimeout = state.battle?.playCardTimeoutSec ?? 45
  return {
    ...state,
    ...overrides,
    uiPhase: "play_cards",
    serverPhase: "PLAY_CARDS",
    timerSec: playTimeout,
    deadlineAt: Date.now() + playTimeout * 1000,
    ...resetRoundPlayState(),
  }
}

/** Итог матча пришёл во время вскрытия — сначала досматриваем карты. */
function shouldDeferMatchEnd(state: DeckDuelState): boolean {
  return state.uiPhase === "round_reveal" && state.lastRoundResult != null
}

/** Если round-result потеряли, собираем вскрытие из последнего раунда матча. */
function roundRevealFromMatchResult(
  state: DeckDuelState,
  match: DeckMatchResultPayload
): DeckDuelState | null {
  const last = match.rounds[match.rounds.length - 1]
  if (!last) return null

  const firstCard = mergeCardShardSlots(
    last.firstCard,
    state.yourDeck.find((item) => item.id === last.firstCard.id) ?? null
  )
  const secondCard = mergeCardShardSlots(
    last.secondCard,
    state.yourDeck.find((item) => item.id === last.secondCard.id) ?? null
  )
  const usedIds = [...state.usedCardIds]
  for (const id of [firstCard.id, secondCard.id]) {
    if (!usedIds.includes(id)) usedIds.push(id)
  }

  const lastRoundResult: DeckRoundResultPayload = {
    battleId: match.battleId,
    round: last.round,
    roundWinnerUserId: last.roundWinnerUserId,
    roundOutcome: last.roundOutcome,
    firstCard,
    secondCard,
    firstCombat: last.firstCombat,
    secondCombat: last.secondCombat,
    yourRoundWins: match.yourRoundWins,
    opponentRoundWins: match.opponentRoundWins,
    yourRemainingCards: Math.max(0, state.yourDeck.length - usedIds.length),
    opponentRemainingCards: Math.max(
      0,
      state.yourDeck.length - match.rounds.length
    ),
    phase: "MATCH_END",
    currentRound: last.round,
    firstPlayerUserId: last.firstPlayerUserId,
  }

  return {
    ...state,
    uiPhase: "round_reveal",
    serverPhase: "MATCH_END",
    usedCardIds: usedIds,
    yourRoundWins: match.yourRoundWins,
    opponentRoundWins: match.opponentRoundWins,
    currentRound: last.round,
    firstPlayerUserId:
      last.firstPlayerUserId > 0
        ? last.firstPlayerUserId
        : state.firstPlayerUserId,
    lastRoundResult,
    matchResult: match,
    yourRemainingCards: lastRoundResult.yourRemainingCards,
    opponentRemainingCards: lastRoundResult.opponentRemainingCards,
    timerSec: 0,
    deadlineAt: null,
    socketReconnecting: false,
    opponentDisconnected: false,
    ...resetRoundPlayState(),
  }
}

function resumeToBattleStarted(
  payload: DeckBattleResumePayload
): DeckBattleStartedPayload {
  return {
    battleId: payload.battleId,
    mode: payload.mode,
    deckSearchMode: payload.deckSearchMode,
    colorRank: payload.colorRank,
    you: payload.you,
    opponent: payload.opponent,
    yourDeck: payload.yourDeck,
    phase: payload.phase,
    currentRound: payload.currentRound,
    yourRoundWins: payload.yourRoundWins,
    opponentRoundWins: payload.opponentRoundWins,
    roundsToWin: payload.roundsToWin,
    playCardTimeoutSec: payload.playCardTimeoutSec,
  }
}

function usedIdsFromResume(payload: DeckBattleResumePayload): string[] {
  const remainingIds = new Set(payload.yourRemainingDeck.map((c) => c.id))
  // Рука = yourRemainingDeck: всё, чего нет в remaining (кроме pending), — уже сыграно.
  const pendingId = payload.yourPendingCardId
  return payload.yourDeck
    .filter((card) => {
      if (remainingIds.has(card.id)) return false
      if (pendingId && card.id === pendingId) return false
      return true
    })
    .map((card) => card.id)
}

function findCardInResume(
  payload: DeckBattleResumePayload,
  cardId: string | null
): BattleCardView | null {
  if (!cardId) return null
  return (
    payload.yourDeck.find((c) => c.id === cardId) ??
    payload.yourRemainingDeck.find((c) => c.id === cardId) ??
    null
  )
}

export function deckDuelReducer(
  state: DeckDuelState,
  action: DeckDuelAction
): DeckDuelState {
  switch (action.type) {
    case "RESET":
      return { ...initialDeckDuelState }

    case "DECK_BATTLE_STARTED": {
      const payload = action.payload
      // Не перезапускаем уже завершённый матч тем же battleId.
      if (
        state.matchResult?.battleId === payload.battleId ||
        (state.uiPhase === "match_end" &&
          state.battle?.battleId === payload.battleId)
      ) {
        return state
      }

      const serverPhase = payload.phase ?? "PLAY_CARDS"
      const currentRound = payload.currentRound ?? 1
      const playTimeout = payload.playCardTimeoutSec ?? 45
      const base: DeckDuelState = {
        ...initialDeckDuelState,
        battle: payload,
        yourDeck: payload.yourDeck,
        roundsToWin: payload.roundsToWin,
        currentRound,
        yourRoundWins: payload.yourRoundWins ?? 0,
        opponentRoundWins: payload.opponentRoundWins ?? 0,
        firstPlayerUserId: payload.firstPlayerUserId ?? null,
        yourRemainingCards: payload.yourDeck.length,
        opponentRemainingCards: payload.yourDeck.length,
        serverPhase,
      }

      if (serverPhase === "MATCH_END") {
        return {
          ...base,
          uiPhase: "match_end",
          timerSec: 0,
          deadlineAt: null,
        }
      }

      return {
        ...base,
        uiPhase: "play_cards",
        serverPhase: "PLAY_CARDS",
        timerSec: playTimeout,
        deadlineAt: Date.now() + playTimeout * 1000,
      }
    }

    case "DECK_BATTLE_SYNC": {
      const payload = action.payload
      if (
        state.uiPhase === "match_end" ||
        state.uiPhase === "round_reveal" ||
        state.matchResult ||
        state.serverPhase === "MATCH_END"
      ) {
        return state
      }

      const serverPhase = payload.phase ?? state.serverPhase
      const firstId =
        resolveFirstPlayerUserId(
          payload.firstPlayerUserId ?? 0,
          state.firstPlayerUserId
        ) ?? state.firstPlayerUserId

      if (
        (state.uiPhase === "play_cards" ||
          state.uiPhase === "play_cards_waiting") &&
        serverPhase === "PLAY_CARDS"
      ) {
        return {
          ...state,
          battle: payload,
          firstPlayerUserId: firstId,
          currentRound: payload.currentRound ?? state.currentRound,
          yourRoundWins: payload.yourRoundWins ?? state.yourRoundWins,
          opponentRoundWins:
            payload.opponentRoundWins ?? state.opponentRoundWins,
        }
      }

      if (serverPhase === "PLAY_CARDS") {
        const playTimeout = payload.playCardTimeoutSec ?? 45
        return {
          ...state,
          battle: payload,
          serverPhase,
          uiPhase: state.yourPlayedCard ? "play_cards_waiting" : "play_cards",
          firstPlayerUserId: firstId,
          currentRound: payload.currentRound ?? state.currentRound,
          yourRoundWins: payload.yourRoundWins ?? state.yourRoundWins,
          opponentRoundWins:
            payload.opponentRoundWins ?? state.opponentRoundWins,
          timerSec: playTimeout,
          deadlineAt: Date.now() + playTimeout * 1000,
        }
      }

      return { ...state, battle: payload, firstPlayerUserId: firstId }
    }

    case "DECK_BATTLE_RESUME": {
      const payload = action.payload
      // После победы resume не должен возвращать в play-фазу.
      if (state.uiPhase === "match_end" || state.matchResult) {
        return state
      }
      if (state.serverPhase === "MATCH_END" && state.lastRoundResult) {
        return state
      }

      const battle = resumeToBattleStarted(payload)
      const usedCardIds = usedIdsFromResume(payload)
      const pendingCard = findCardInResume(payload, payload.yourPendingCardId)
      const completedRounds = payload.rounds.length
      const deckSize = Math.max(payload.yourDeck.length, 1)
      const yourRemainingCards = payload.yourRemainingDeck.length
      const opponentRemainingCards = Math.max(0, deckSize - completedRounds)

      const base: DeckDuelState = {
        ...state,
        battle,
        yourDeck: payload.yourDeck,
        usedCardIds,
        roundsToWin: payload.roundsToWin,
        currentRound: payload.currentRound,
        yourRoundWins: payload.yourRoundWins,
        opponentRoundWins: payload.opponentRoundWins,
        yourPlayedCard: pendingCard,
        opponentReady: payload.opponentCardReady,
        yourRemainingCards,
        opponentRemainingCards,
        socketReconnecting: false,
        opponentDisconnected: payload.opponentDisconnected,
        opponentReconnectGraceSec: payload.reconnectGraceSec,
        cancelled: null,
        matchResult: null,
        lastRoundResult: null,
      }

      if (payload.phase === "MATCH_END") {
        return {
          ...base,
          uiPhase: "match_end",
          serverPhase: "MATCH_END",
          timerSec: 0,
          deadlineAt: null,
        }
      }

      const secondsLeft = Math.max(0, payload.secondsLeft)
      return {
        ...base,
        uiPhase: pendingCard ? "play_cards_waiting" : "play_cards",
        serverPhase: "PLAY_CARDS",
        timerSec: secondsLeft,
        deadlineAt: Date.now() + secondsLeft * 1000,
      }
    }

    case "PLAY_CARD":
      return {
        ...state,
        yourPlayedCard: action.payload.card,
        uiPhase: "play_cards_waiting",
      }

    case "PLAY_CARD_FAILED":
      if (!state.yourPlayedCard) return state
      return {
        ...state,
        yourPlayedCard: null,
        uiPhase: "play_cards",
      }

    case "PLAY_CARD_WAITING":
      return {
        ...state,
        uiPhase: state.yourPlayedCard ? "play_cards_waiting" : state.uiPhase,
      }

    case "OPPONENT_CARD_READY":
      return {
        ...state,
        opponentReady: true,
      }

    case "SOCKET_RECONNECTING":
      // На экране победы/отмены не уходим в «Восстановление боя...»
      if (
        state.uiPhase === "match_end" ||
        state.uiPhase === "cancelled" ||
        state.uiPhase === "round_reveal"
      ) {
        return state
      }
      return {
        ...state,
        socketReconnecting: true,
      }

    case "SOCKET_RECONNECTED":
      return {
        ...state,
        socketReconnecting: false,
      }

    case "OPPONENT_DISCONNECTED":
      return {
        ...state,
        opponentDisconnected: true,
        opponentReconnectGraceSec: action.payload.graceSec,
      }

    case "OPPONENT_RECONNECTED":
      return {
        ...state,
        opponentDisconnected: false,
      }

    case "ROUND_RESULT": {
      const { payload, youUserId } = action
      const usedIds = [...state.usedCardIds]
      const firstPlayerId =
        resolveFirstPlayerUserId(
          payload.firstPlayerUserId,
          state.firstPlayerUserId
        ) ?? payload.firstPlayerUserId
      const firstIsYou = resolveFirstSlotIsYou(payload, youUserId, {
        knownFirstPlayerUserId: firstPlayerId,
        yourDeck: state.yourDeck,
        yourPlayedCardId: state.yourPlayedCard?.id ?? null,
      })
      const firstCard = mergeCardShardSlots(
        payload.firstCard,
        state.yourDeck.find((item) => item.id === payload.firstCard.id) ?? null
      )
      const secondCard = mergeCardShardSlots(
        payload.secondCard,
        state.yourDeck.find((item) => item.id === payload.secondCard.id) ?? null
      )
      const yourCardId = firstIsYou ? firstCard.id : secondCard.id
      const oppCardId = firstIsYou ? secondCard.id : firstCard.id
      if (!usedIds.includes(yourCardId)) usedIds.push(yourCardId)
      if (!usedIds.includes(oppCardId)) usedIds.push(oppCardId)

      const roundWins = incrementRoundWins(
        state,
        payload.roundWinnerUserId,
        youUserId
      )
      const lastRoundResult: DeckRoundResultPayload = {
        ...payload,
        firstCard,
        secondCard,
        firstPlayerUserId:
          firstPlayerId > 0 ? firstPlayerId : state.firstPlayerUserId ?? 0,
        yourRoundWins: roundWins.yourRoundWins,
        opponentRoundWins: roundWins.opponentRoundWins,
      }

      return {
        ...state,
        uiPhase: "round_reveal",
        serverPhase: payload.phase === "MATCH_END" ? "MATCH_END" : "PLAY_CARDS",
        usedCardIds: usedIds,
        yourRoundWins: roundWins.yourRoundWins,
        opponentRoundWins: roundWins.opponentRoundWins,
        currentRound: payload.currentRound,
        firstPlayerUserId:
          firstPlayerId > 0 ? firstPlayerId : state.firstPlayerUserId,
        lastRoundResult,
        yourRemainingCards: payload.yourRemainingCards,
        opponentRemainingCards: payload.opponentRemainingCards,
        timerSec: 0,
        deadlineAt: null,
        socketReconnecting: false,
        ...resetRoundPlayState(),
      }
    }

    case "ROUND_REVEAL_DONE": {
      if (state.serverPhase === "MATCH_END" || state.matchResult) {
        return {
          ...state,
          uiPhase: "match_end",
          serverPhase: "MATCH_END",
          lastRoundResult: null,
          socketReconnecting: false,
        }
      }

      return startPlayCardsPhase(state, {
        lastRoundResult: null,
      })
    }

    case "MATCH_RESULT": {
      const match = action.payload

      if (shouldDeferMatchEnd(state)) {
        return {
          ...state,
          serverPhase: "MATCH_END",
          matchResult: match,
          yourRoundWins: match.yourRoundWins,
          opponentRoundWins: match.opponentRoundWins,
          socketReconnecting: false,
          opponentDisconnected: false,
          timerSec: 0,
          deadlineAt: null,
        }
      }

      // Пропущенный round-result: всё равно показываем карты последнего раунда.
      if (
        state.uiPhase === "play_cards" ||
        state.uiPhase === "play_cards_waiting" ||
        state.uiPhase === "idle"
      ) {
        const reveal = roundRevealFromMatchResult(state, match)
        if (reveal) return reveal
      }

      return {
        ...state,
        uiPhase: "match_end",
        serverPhase: "MATCH_END",
        matchResult: match,
        yourRoundWins: match.yourRoundWins,
        opponentRoundWins: match.opponentRoundWins,
        lastRoundResult: null,
        timerSec: 0,
        deadlineAt: null,
        socketReconnecting: false,
        opponentDisconnected: false,
      }
    }

    case "BATTLE_CANCELLED":
      return {
        ...state,
        uiPhase: "cancelled",
        cancelled: action.payload,
        timerSec: 0,
        deadlineAt: null,
        socketReconnecting: false,
        opponentDisconnected: false,
      }

    case "TICK_TIMER": {
      if (!state.deadlineAt) return state
      const remaining = Math.max(
        0,
        Math.ceil((state.deadlineAt - Date.now()) / 1000)
      )
      if (remaining === state.timerSec) return state
      return { ...state, timerSec: remaining }
    }

    default:
      return state
  }
}

export function getDeckAvailableCards(state: DeckDuelState): BattleCardView[] {
  return availableCards(state)
}

export function isDeckPhaseTimed(state: DeckDuelState): boolean {
  return (
    state.uiPhase === "play_cards" || state.uiPhase === "play_cards_waiting"
  )
}

/** Локальный таймер истёк — не слать ходы на сервер (он уже отклонит). */
export function isDeckInputLocked(state: DeckDuelState): boolean {
  if (state.socketReconnecting) return true
  if (!isDeckPhaseTimed(state) || state.deadlineAt == null) return false
  return state.timerSec <= 0
}

export function isYourTurnToPlay(state: DeckDuelState): boolean {
  if (isDeckInputLocked(state)) return false
  return state.uiPhase === "play_cards" && !state.yourPlayedCard
}
